import React, { FormEvent } from "react";
import { Input } from "@twilio-paste/core/input";
import { Label } from "@twilio-paste/core/label";
import { Box } from "@twilio-paste/core/box";
import { TextArea } from "@twilio-paste/core/textarea";
import { Button } from "@twilio-paste/core/button";
import { useDispatch, useSelector } from "react-redux";
import { Text } from "@twilio-paste/core/text";

import { sessionDataHandler } from "../../sessionDataHandler";
import {
    addNotification,
    changeEngagementPhase,
    updatePreEngagementData,
    updatePreEngagementFieldValue
} from "../../store/actions/genericActions";
import { initSession } from "../../store/actions/initActions";
import { AppState, EngagementPhase } from "../../store/definitions";
import { Header } from "../Header";
import { notifications } from "../../notifications";
import { NotificationBar } from "../NotificationBar";
import { introStyles, fieldStyles, titleStyles, formStyles } from "../styles/PreEngagementFormPhase.styles";
import { Field } from "../Field";

export const PreEngagementFormPhase = () => {
    const { name, email, query, fields, preEngagementFields, glanceConfiguration } = useSelector((state: AppState) => ({
        name: state.session.preEngagementData?.name,
        email: state.session.preEngagementData?.email,
        query: state.session.preEngagementData?.query,
        fields: state.session.preEngagementData?.fields,
        preEngagementFields: state.config.preEngagementFields,
        glanceConfiguration: state.config.glance
    }));

    const dispatch = useDispatch();

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        dispatch(changeEngagementPhase({ phase: EngagementPhase.Loading }));
        try {
            const data = await sessionDataHandler.fetchAndStoreNewSession({
                friendlyName: name,
                query,
                formData: {
                    friendlyName: name,
                    email,
                    query,
                    ...fields
                }
            });
            dispatch(
                initSession({
                    token: data.token,
                    conversationSid: data.conversationSid,
                    glanceConfiguration
                })
            );
        } catch (err) {
            dispatch(addNotification(notifications.failedToInitSessionNotification((err as Error).message)));
            dispatch(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };

    const renderOptionalFields = React.useCallback(() => {
        if (preEngagementFields === undefined || preEngagementFields.length === 0) {
            return null;
        }

        return preEngagementFields.map((field) => (
            <Field
                boxStyle={fieldStyles}
                key={field.name}
                field={field}
                value={fields?.[field.name]}
                onChange={(e) => dispatch(updatePreEngagementFieldValue(field.name, e))}
            />
        ));
    }, [preEngagementFields, fields, dispatch]);

    return (
        <>
            <Header />
            <NotificationBar />
            <Box as="form" data-test="pre-engagement-chat-form" onSubmit={handleSubmit} {...formStyles}>
                <Text {...titleStyles} as="h3">
                    Hi there!
                </Text>
                <Text {...introStyles} as="p">
                    We&#39;re here to help. Please give us some info to get started.
                </Text>
                <Box {...fieldStyles}>
                    <Label htmlFor="name">Name</Label>
                    <Input
                        type="text"
                        placeholder="Please enter your name"
                        name="name"
                        id="name"
                        data-test="pre-engagement-chat-form-name-input"
                        value={name}
                        onChange={(e) => dispatch(updatePreEngagementData({ name: e.target.value }))}
                        required
                    />
                </Box>
                <Box {...fieldStyles}>
                    <Label htmlFor="email">Email address</Label>
                    <Input
                        type="email"
                        placeholder="Please enter your email address"
                        name="email"
                        id="email"
                        data-test="pre-engagement-chat-form-email-input"
                        value={email}
                        onChange={(e) => dispatch(updatePreEngagementData({ email: e.target.value }))}
                        required
                    />
                </Box>

                {renderOptionalFields()}

                <Box {...fieldStyles}>
                    <Label htmlFor="query">How can we help you?</Label>
                    <TextArea
                        placeholder="Ask a question"
                        name="query"
                        id="query"
                        data-test="pre-engagement-chat-form-query-textarea"
                        value={query}
                        onChange={(e) => dispatch(updatePreEngagementData({ query: e.target.value }))}
                        onKeyPress={handleKeyPress}
                        required
                    />
                </Box>

                <Button variant="primary" type="submit" data-test="pre-engagement-start-chat-button">
                    Start chat
                </Button>
            </Box>
        </>
    );
};
